/**
 * Vocabulário do formulário de cadastro de fornecedor.
 *
 * Mora aqui porque DUAS telas precisam do mesmo de-para: o formulário público
 * (`FornecedorPublico/CadastroFornecedor`), que oferece as opções, e a fila de
 * revisão (`Fornecedor/PainelSubmissaoFornecedor`), que mostra ao revisor o que o
 * fornecedor escolheu. Enquanto o mapa vivia só no formulário, a revisão exibia o
 * código cru — o revisor lia `ate_24h` e `cbs_ibs` em vez do rótulo.
 */

export const TIPOS_CONTA = [
    // Opção vazia como em TIPOS_PIX e PRAZOS_XML: sem ela o formulário mandava
    // 'corrente' mesmo com a seção bancária intocada, e a tela de revisão marcava
    // "alterado" numa declaração que não houve.
    { valor: '', rotulo: 'Não informado' },
    { valor: 'corrente', rotulo: 'Conta corrente' },
    { valor: 'poupanca', rotulo: 'Conta poupança' },
    { valor: 'pagamento', rotulo: 'Conta pagamento' },
];

export const TIPOS_PIX = [
    { valor: '', rotulo: 'Sem PIX' },
    { valor: 'cnpj', rotulo: 'CNPJ' },
    { valor: 'cpf', rotulo: 'CPF' },
    { valor: 'email', rotulo: 'E-mail' },
    { valor: 'telefone', rotulo: 'Telefone' },
    { valor: 'aleatoria', rotulo: 'Chave aleatória' },
];

export const PAPEIS_CONTATO = [
    { valor: 'comercial', rotulo: 'Comercial' },
    { valor: 'fiscal', rotulo: 'Fiscal' },
    { valor: 'financeiro', rotulo: 'Financeiro' },
    { valor: 'cbs_ibs', rotulo: 'Responsável por CBS/IBS' },
];

// O anexo lista as opções e ainda prevê "Outro: ___".
export const PRAZOS_XML = [
    { valor: '', rotulo: 'Não informado' },
    { valor: 'no_ato', rotulo: 'No ato da emissão' },
    { valor: 'ate_24h', rotulo: 'Até 24 horas' },
    { valor: 'ate_48h', rotulo: 'Até 48 horas' },
    { valor: 'outro', rotulo: 'Outro' },
];

/**
 * Rótulo de um código, com o próprio código como fallback.
 *
 * Devolver o código quando ele não está no mapa é deliberado: um valor gravado por
 * uma versão anterior do formulário continua legível, em vez de virar traço e
 * sumir da tela de revisão.
 */
export const rotuloDe = (lista, valor) => {
    if (valor === null || valor === undefined || valor === '') return '';
    const achado = lista.find(item => item.valor === valor);
    return achado ? achado.rotulo : String(valor);
};

/**
 * Converte a lista para o formato do `ReactSelect` (`{value, label}`).
 *
 * As listas ficam em `{valor, rotulo}` porque é assim que o resto da tela as lê;
 * a conversão mora aqui para as duas telas não repetirem o `map`.
 *
 * A entrada de valor vazio ("Não informado") fica de fora: como opção, escolhê-la
 * limpava a seleção. Ela vira o placeholder do select (`placeholderDe`), e quem
 * quer limpar usa o X do `isClearable`.
 */
export const opcoesDe = (lista) => lista
    .filter(item => item.valor !== '')
    .map(item => ({ value: item.valor, label: item.rotulo }));

/** Rótulo da entrada vazia da lista, para o placeholder do select; `null` se não houver. */
export const placeholderDe = (lista) => {
    const vazia = lista.find(item => item.valor === '');
    return vazia ? vazia.rotulo : null;
};

/**
 * `opcoesDe` mais os valores JÁ GRAVADOS que saíram da lista, cada um como opção
 * própria.
 *
 * Um select multi resolve o que exibir filtrando as opções pelos valores
 * guardados: valor fora da lista não acha opção, some da tela, e o próximo save
 * grava sem ele — apaga em silêncio o que ninguém mandou apagar. Foi o que
 * aconteceria com `mdfe` quando ele saiu de `DOCUMENTOS_FISCAIS`.
 *
 * Aqui ele reaparece com o próprio código por rótulo, do mesmo jeito que o
 * `rotuloDe` devolve o código quando não conhece o valor. Fica visível, editável,
 * e some quando o operador o desmarcar — que é uma decisão, não um efeito
 * colateral.
 */
export const opcoesComValoresGravados = (lista, valores) => {
    const base = opcoesDe(lista);
    const conhecidos = base.map(opcao => opcao.value);

    const orfaos = (valores || [])
        .filter(valor => valor !== null && valor !== undefined && valor !== '')
        .filter(valor => !conhecidos.includes(valor))
        .map(valor => ({ value: valor, label: String(valor) }));

    return orfaos.length > 0 ? [...base, ...orfaos] : base;
};

/**
 * Caminho do campo (a chave que o `nao_aplica` grava, ver `naKey` do `CampoNA`)
 * para o rótulo que o fornecedor viu no formulário. Usado para MOSTRAR ao
 * operador quais perguntas ele marcou como não aplicáveis — a fila de revisão já
 * fazia essa tradução por rótulo cru; agora que a chave é o caminho do campo, o
 * mapa é quem faz a tradução.
 */
export const ROTULOS_CAMPOS_NAO_APLICA = {
    natureza: 'Natureza do cadastro',
    razaosocial: 'Razão social',
    nomefantasia: 'Nome fantasia',
    cnpj: 'CNPJ',
    inscricaoestadual: 'Inscrição estadual',
    inscricaomunicipal: 'Inscrição municipal',
    cnae_principal: 'CNAE principal',
    cnaes_secundarios: 'CNAEs secundários',
    porte: 'Porte da empresa',
    data_constituicao: 'Data de constituição',
    'endereco.cep': 'CEP',
    'endereco.logradouro': 'Logradouro',
    'endereco.numero': 'Número',
    'endereco.complemento': 'Complemento',
    'endereco.bairro': 'Bairro',
    'endereco.uf': 'UF',
    'endereco.pais': 'País',
    'endereco.municipio': 'Município',
    email_xml: 'E-mail para envio de XML e PDF',
    prazo_envio_xml: 'Prazo de envio do XML',
    'banco.nome_banco': 'Banco',
    'banco.agencia': 'Agência',
    'banco.conta': 'Conta',
    'banco.tipo_conta': 'Tipo de conta',
    'banco.pix_tipo': 'Tipo de chave PIX',
    'banco.pix_chave': 'Chave PIX',
    'banco.titular': 'Favorecido',
    'banco.cnpj_favorecido': 'CNPJ do favorecido',
    'fiscal.tipo_fornecedor': 'Tipo de fornecedor',
    'fiscal.itens_fornecidos': 'Principais itens fornecidos',
    'fiscal.ncm': 'NCM principais (mercadorias)',
    'fiscal.nbs': 'NBS principal (serviços)',
    'fiscal.locais_execucao': 'Municípios e UFs onde executa',
    'fiscal.regime_irpj': 'Regime IRPJ/CSLL',
    'fiscal.regime_irpj_fundamento': 'Fundamento da imunidade ou isenção',
    'fiscal.regime_ibs_cbs': 'Regime de apuração de IBS/CBS',
    'fiscal.vigencia_opcao': 'Período de vigência da opção (semestre/ano)',
    // Saiu do formulário; fica para ler o `nao_aplica` de submissão antiga.
    'fiscal.vigencia_opcao_fim': 'Vigência da opção — término previsto',
    'fiscal.data_opcao_simples': 'Data da opção no Portal do Simples Nacional',
    'fiscal.opcao_proxima_limite': 'Data-limite da próxima opção',
    'fiscal.regime_especifico': 'Enquadrado em regime específico de IBS/CBS?',
    'fiscal.regime_especifico_outro': 'Qual regime',
    'fiscal.reducao_percentual': 'Redução, alíquota zero, isenção ou imunidade',
    'fiscal.destaque_documentos': 'Destaque nos documentos',
    'fiscal.cst': 'CST',
    'fiscal.cclasstrib': 'cClassTrib',
    'fiscal.credito_presumido_aplica': 'Aplica crédito presumido?',
    // Saiu do formulário; fica para ler o `nao_aplica` de submissão antiga.
    'fiscal.credito_presumido': 'Em que hipótese (crédito presumido)',
    'fiscal.diferimento_aplica': 'Opera com diferimento ou suspensão?',
    'fiscal.diferimento_suspensao': 'Em que hipótese (diferimento ou suspensão)',
    'fiscal.retencoes_outras': 'Outras retenções',
    'fiscal.observacao_fiscal': 'Observações sobre retenções na transição',
    'fiscal.data_transicao_iva': 'Data da transição do IVA',
    assinante_nome: 'Nome do representante legal',
    assinante_cpf: 'CPF do representante legal',
    assinante_cargo: 'Cargo do representante legal',
    assinatura_local: 'Local da assinatura',
    assinatura_data: 'Data da assinatura',
};

/**
 * Rótulo de um caminho marcado como "não se aplica" — resolve os fixos pelo mapa
 * acima e os de upload (\`documentos.<tipo>\`) contra \`TIPOS_DOCUMENTO\`, já que
 * esses nascem de um laço e não têm entrada própria no mapa.
 */
export const rotuloDoCaminhoNaoAplica = (caminho) => {
    if (ROTULOS_CAMPOS_NAO_APLICA[caminho]) {
        return ROTULOS_CAMPOS_NAO_APLICA[caminho];
    }

    if (String(caminho).indexOf('documentos.') === 0) {
        return rotuloDe(TIPOS_DOCUMENTO, caminho.slice('documentos.'.length));
    }

    return caminho;
};

/** Uma linha de contato legível: "Comercial · Ana · ana@x.com · 11 99999-0000". */
export const rotuloContato = (contato) => {
    if (!contato) return '—';

    const partes = [
        rotuloDe(PAPEIS_CONTATO, contato.cargo),
        contato.nome,
        // Cargo real da pessoa, distinto da área — ver `contatoVazio`.
        contato.funcao,
        contato.email,
        contato.celular,
    ].filter(Boolean);

    return partes.length > 0 ? partes.join(' · ') : '—';
};


// ─────────────────────────────────────────────────────────────────────────────
// Seções 4 a 7 e 9 do anexo. Os `valor` são exatamente as strings que as colunas
// de `fornecedor_classificacao_fiscal` e `fornecedor_documento` aceitam — mudar um aqui sem
// mudar a migration grava lixo que o revisor só descobre lendo o banco.
// ─────────────────────────────────────────────────────────────────────────────

// Cabeçalho do anexo
// Seção 1 — porte da empresa, lista literal do anexo.
export const PORTES_EMPRESA = [
    { valor: '', rotulo: 'Não informado' },
    { valor: 'mei', rotulo: 'MEI' },
    { valor: 'me', rotulo: 'ME' },
    { valor: 'epp', rotulo: 'EPP' },
    { valor: 'demais', rotulo: 'Demais' },
];

export const NATUREZAS_CADASTRO = [
    { valor: 'cadastro_inicial', rotulo: 'Cadastro inicial' },
    { valor: 'atualizacao', rotulo: 'Atualização cadastral' },
    { valor: 'revisao_periodica', rotulo: 'Revisão periódica' },
];

// Por onde o CNPJ digitado levou o formulário: `fornecedor_submissao.caminho`, gravado pelo servidor.
export const CAMINHOS_SUBMISSAO = [
    { valor: 'atualizacao', rotulo: 'Atualização' },
    { valor: 'novo', rotulo: 'Novo fornecedor' },
    { valor: 'troca_cnpj', rotulo: 'Troca de CNPJ' },
];

// Seção 4 — lista literal do anexo. "transportadora" é categoria própria porque
// emite CT-e/MDF-e e tem regra de apuração distinta, não é "serviços".
export const TIPOS_FORNECEDOR = [
    { valor: '', rotulo: 'Não informado' },
    { valor: 'mercadorias', rotulo: 'Mercadorias' },
    { valor: 'servicos', rotulo: 'Serviços' },
    { valor: 'mercadorias_servicos', rotulo: 'Mercadorias e serviços' },
    { valor: 'transportadora', rotulo: 'Transportadora' },
];

/**
 * Documentos fiscais que o fornecedor emite — lista literal da área.
 *
 * O rótulo carrega o MODELO porque é assim que o financeiro do fornecedor
 * identifica o documento no ERP dele: "modelo 55" é reconhecível, "NF-e" sozinho
 * confunde com NFS-e para quem não é da área fiscal.
 *
 * A lista é literal, na ordem em que a área mandou — inclusive `recibo_rpa`.
 *
 * MDF-e saiu porque não está nela — manifesto acompanha carga, não é documento
 * que se emite numa venda. Valor gravado antes disso não some da tela: ver
 * `opcoesComValoresGravados`.
 */
export const DOCUMENTOS_FISCAIS = [
    { valor: 'nfe', rotulo: 'NF-e — nota fiscal eletrônica, modelo 55 (mercadoria)' },
    { valor: 'nfce', rotulo: 'NFC-e — consumidor final, modelo 65' },
    { valor: 'nfse', rotulo: 'NFS-e — nota de serviço, padrão nacional' },
    { valor: 'cte', rotulo: 'CT-e — conhecimento de transporte, modelo 57' },
    { valor: 'cte_os', rotulo: 'CT-e OS — transporte de pessoas, modelo 67 (fretamento, transfer)' },
    { valor: 'bpe', rotulo: 'BP-e — bilhete de passagem eletrônico, modelo 63' },
    { valor: 'nfcom', rotulo: 'NFCom — comunicação, modelo 62' },
    { valor: 'recibo_rpa', rotulo: 'Recibo / RPA — pessoa física, sem emissão de nota' },
    { valor: 'outro', rotulo: 'Outro' },
];

// Seção 5
// Seção 5.1 — lista literal do anexo. `lucro_arbitrado` chegou a existir aqui e foi
// removido: não está no anexo, e opção inventada em campo tributário vira declaração
// que o revisor não tem contra o quê conferir.
export const REGIMES_IRPJ = [
    { valor: '', rotulo: 'Não informado' },
    { valor: 'simples', rotulo: 'Simples Nacional' },
    { valor: 'mei', rotulo: 'MEI' },
    { valor: 'lucro_presumido', rotulo: 'Lucro Presumido' },
    { valor: 'lucro_real', rotulo: 'Lucro Real' },
    { valor: 'imune_isento', rotulo: 'Imune / Isento' },
    { valor: 'outro', rotulo: 'Outro' },
];

// Seção 5.3 — nove regimes, lista fechada.
export const REGIMES_ESPECIFICOS = [
    // Vazio é "não respondi"; `nao_aplica` é "respondi que não tenho".
    //
    // O rótulo "Não se aplica" estava no valor vazio, colapsando as duas respostas —
    // e é a 5.3 que empurra a classificação de crédito da seção 11 para "Reduzido".
    // Decidir crédito sobre um silêncio é decidir sobre uma dúvida.
    { valor: '', rotulo: 'Não informado' },
    { valor: 'nao_aplica', rotulo: 'Não se aplica' },
    { valor: 'hotelaria', rotulo: 'Hotelaria' },
    { valor: 'bares_restaurantes', rotulo: 'Bares e restaurantes' },
    { valor: 'agencias_turismo', rotulo: 'Agências de turismo' },
    { valor: 'parques_diversao', rotulo: 'Parques de diversão' },
    { valor: 'transporte_passageiros', rotulo: 'Transporte de passageiros' },
    { valor: 'servicos_profissionais', rotulo: 'Serviços profissionais regulamentados' },
    { valor: 'cooperativa', rotulo: 'Cooperativa' },
    { valor: 'zfm_alc', rotulo: 'ZFM / ALC' },
    { valor: 'outro', rotulo: 'Outro' },
];

// As duas do Simples com o texto da área: para o optante, a pergunta é a forma de
// recolhimento do IBS/CBS, dentro ou fora do DAS. Os valores não mudam.
export const REGIMES_IBS_CBS = [
    { valor: '', rotulo: 'Não informado' },
    { valor: 'regular', rotulo: 'Regime regular' },
    { valor: 'simples_nacional', rotulo: 'Dentro do DAS (Simples tradicional)' },
    { valor: 'simples_regime_regular', rotulo: 'Regime regular, fora do DAS (Simples híbrido)' },
    { valor: 'mei', rotulo: 'MEI' },
];

/**
 * Semestres do "Período de vigência da opção (semestre/ano)", do ano anterior aos dois
 * seguintes.
 *
 * O valor é o PRIMEIRO DIA do semestre, que é o que `vigencia_opcao` (date) guarda. O fim
 * não tem coluna: é sempre 30/06 ou 31/12 do semestre que começa ali.
 */
export const semestresVigencia = (anoBase = new Date().getFullYear()) => {
    const lista = [{ valor: '', rotulo: 'Não informado' }];
    for (let ano = anoBase - 1; ano <= anoBase + 2; ano++) {
        lista.push({ valor: `${ano}-01-01`, rotulo: `1º semestre/${ano}` });
        lista.push({ valor: `${ano}-07-01`, rotulo: `2º semestre/${ano}` });
    }
    return lista;
};

/**
 * "1º semestre/2027" a partir do início gravado. Data que não abre semestre — gravada antes
 * de a vigência virar semestre — aparece como data, em vez de virar um semestre que ninguém
 * declarou.
 */
export const rotuloDoSemestre = (inicio) => {
    if (!inicio) return '';
    const partes = /^(\d{4})-(01|07)-01/.exec(String(inicio));
    if (partes) return `${partes[2] === '01' ? '1º' : '2º'} semestre/${partes[1]}`;
    return String(inicio).slice(0, 10).split('-').reverse().join('/');
};

/**
 * As opções de semestre mais o início já gravado, quando ele não é uma delas — mesmo motivo
 * do `opcoesComValoresGravados`: valor sem opção some do select, e o próximo save grava sem ele.
 */
export const opcoesSemestre = (gravado) => {
    const base = opcoesDe(semestresVigencia());
    return gravado && !base.some(opcao => opcao.value === gravado)
        ? [...base, { value: gravado, label: rotuloDoSemestre(gravado) }]
        : base;
};

/**
 * "Contribuinte de ICMS?" — os valores são os do `participante.indicadorinscricao`, o
 * "Indicador de Inscrição" do cadastro interno, que é onde a aprovação grava. O domínio
 * inteiro serve para a Revisão mostrar o que está no cadastro, inclusive o Isento, que é
 * do operador; o formulário pergunta só Não/Sim, na ordem do `SIM_NAO` do ISS ao lado.
 */
export const INDICADORES_INSCRICAO = [
    { valor: '', rotulo: 'Não informado' },
    { valor: '9', rotulo: 'Não' },
    { valor: '1', rotulo: 'Sim' },
    { valor: '2', rotulo: 'Isento' },
];
export const CONTRIBUINTE_ICMS = INDICADORES_INSCRICAO.filter(item => item.valor !== '2');

// Seção 6
// Seção 6 — o par Não/Sim do crédito presumido e do diferimento.
export const SIM_NAO = [
    { valor: '', rotulo: 'Não informado' },
    { valor: 'nao', rotulo: 'Não' },
    { valor: 'sim', rotulo: 'Sim' },
];

// Seção 6 — Sim | Não | Parcialmente, como o anexo. `nao_aplicavel` chegou a existir
// aqui e foi removido pelo mesmo motivo do `lucro_arbitrado`: não está no anexo, e
// "não se aplica" é dedutível do regime da 5.2 — não é declaração nova.
export const DESTAQUE_DOCUMENTOS = [
    { valor: '', rotulo: 'Não informado' },
    { valor: 'sim', rotulo: 'Sim' },
    { valor: 'nao', rotulo: 'Não' },
    { valor: 'parcialmente', rotulo: 'Parcialmente' },
];

// Seção 7 — a ordem é a do anexo, e a tela mostra uma linha por tributo.
export const TRIBUTOS_RETENCAO = [
    { valor: 'irrf', rotulo: 'IRRF' },
    { valor: 'pis', rotulo: 'PIS' },
    { valor: 'cofins', rotulo: 'COFINS' },
    { valor: 'csll', rotulo: 'CSLL' },
    { valor: 'inss', rotulo: 'INSS' },
    { valor: 'iss', rotulo: 'ISS' },
    { valor: 'icms_st', rotulo: 'ICMS/ST' },
    { valor: 'funrural_senar', rotulo: 'Funrural / SENAR' },
];

// Seção 9
export const TIPOS_DOCUMENTO = [
    { valor: 'cartao_cnpj', rotulo: 'Cartão CNPJ' },
    { valor: 'contrato_social', rotulo: 'Contrato social' },
    { valor: 'comprovante_ie', rotulo: 'Comprovante de inscrição estadual' },
    { valor: 'comprovante_im', rotulo: 'Comprovante de inscrição municipal' },
    { valor: 'certidao_simples', rotulo: 'Certidão do Simples Nacional' },
    { valor: 'opcao_regime_regular', rotulo: 'Comprovante da opção pelo regime regular' },
    // O anexo separa as esferas: a federal é CND/CPEND e a estadual/municipal é outro
    // documento, com outro emissor e outra validade. Um `cnd` genérico impedia o
    // revisor de saber qual esfera está faltando.
    { valor: 'cnd_federal', rotulo: 'CND Federal (CND / CPEND)' },
    { valor: 'certidao_estadual_municipal', rotulo: 'Certidão Estadual / Municipal' },
    { valor: 'crf_fgts', rotulo: 'CRF / FGTS' },
    { valor: 'comprovante_bancario', rotulo: 'Comprovante bancário' },
    { valor: 'outro', rotulo: 'Outro documento' },
];

// Seção 11 — uso interno. Nunca aparece no formulário público.
export const SITUACOES_CADASTRO = [
    { valor: '', rotulo: 'Não classificado' },
    { valor: 'atualizado', rotulo: 'Atualizado' },
    { valor: 'em_revisao', rotulo: 'Em revisão' },
    { valor: 'pendente', rotulo: 'Pendente' },
    { valor: 'bloqueado', rotulo: 'Bloqueado' },
];

export const CLASSIFICACOES_CREDITO = [
    { valor: '', rotulo: 'Não classificado' },
    { valor: 'integral', rotulo: 'Crédito integral' },
    { valor: 'reduzido', rotulo: 'Crédito reduzido' },
    { valor: 'limitado', rotulo: 'Crédito limitado (Simples)' },
    { valor: 'sem_credito', rotulo: 'Sem crédito' },
];

export const PERIODICIDADES_REVISAO = [
    { valor: '', rotulo: 'Não definida' },
    { valor: '6', rotulo: '6 meses' },
    { valor: '12', rotulo: '12 meses' },
    { valor: '24', rotulo: '24 meses' },
];

/**
 * Estado inicial da seção 7: {aplica, aliquota, observacao} por tributo.
 *
 * A observação é o que o anexo pede — "Sim/Não + alíquota/observação" — e é onde o
 * fornecedor diz o "até quando" de cada retenção na transição. Ela chegou a ser
 * trocada por um select de esfera porque a coluna `esfera_X` já existia; era trocar
 * campo do anexo por conveniência de schema.
 *
 * A `esfera_X` continua existindo e é editada pelo operador na aba Retenções, num
 * input de texto livre rotulado "Esfera / observação" — que, apesar do nome, é campo
 * dele, não do fornecedor.
 *
 * Distinto do `observacao_fiscal`, que é o campo livre único sobre a transição como
 * um todo: aqui é uma nota por tributo.
 */
export const retencoesVazias = () => TRIBUTOS_RETENCAO.reduce((acc, t) => {
    acc[t.valor] = { aplica: false, aliquota: '', observacao: '' };
    return acc;
}, {});

/**
 * CNPJ válido pelos dígitos verificadores — numérico E alfanumérico.
 *
 * Espelha o `Util::cnpjValido()` do back, e a duplicação é deliberada: o servidor
 * precisa validar porque a rota é pública, e a tela precisa validar porque descobrir
 * o erro só depois de preencher dez seções é castigo. Se um dos dois mudar, o outro
 * tem que acompanhar — a regra é da Receita, não nossa, então isso é raro.
 *
 * A conta é a de sempre (módulo 11), trocando o dígito pelo ASCII do caractere menos
 * 48. Como '0'..'9' são ASCII 48..57, o formato numérico é caso particular do
 * alfanumérico e um algoritmo cobre os dois. Os dois DVs são sempre numéricos.
 */
export const cnpjValido = (valor) => {
    const cnpj = String(valor || '').toUpperCase().replace(/[^0-9A-Z]/g, '');

    if (cnpj.length !== 14) return false;
    if (!/^\d{2}$/.test(cnpj.slice(12))) return false;
    // `11111111111111` fecha o módulo 11 e não é CNPJ de ninguém.
    if (/^(.)\1{13}$/.test(cnpj)) return false;

    const digito = (quantidade, pesos) => {
        let soma = 0;
        for (let i = 0; i < quantidade; i += 1) {
            soma += (cnpj.charCodeAt(i) - 48) * pesos[i];
        }
        const resto = soma % 11;
        return resto < 2 ? 0 : 11 - resto;
    };

    if (Number(cnpj[12]) !== digito(12, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])) return false;

    return Number(cnpj[13]) === digito(13, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
};

/**
 * CPF válido pelos dígitos verificadores — espelho do `Util::cpfValido()` do back.
 *
 * A seção 10 do anexo pede o CPF do representante legal. CPF que não fecha o módulo
 * 11 não identifica ninguém, e guardá-lo daria a aparência de identificação sem a
 * identificação.
 */
export const cpfValido = (valor) => {
    const cpf = String(valor || '').replace(/\D/g, '');

    if (cpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpf)) return false;

    for (let posicao = 9; posicao < 11; posicao += 1) {
        let soma = 0;
        for (let i = 0; i < posicao; i += 1) {
            soma += Number(cpf[i]) * ((posicao + 1) - i);
        }
        if (Number(cpf[posicao]) !== ((10 * soma) % 11) % 10) return false;
    }

    return true;
};

/** `1234567` -> `1,2 MB`. Tamanho em bytes, como o back devolve. */
export const formatarTamanho = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
};
